/**
 * Client-minted Natalie visitor id — the correlation key for the conversion funnel.
 *
 * Created on the public invite page *before* any identity exists (works for
 * real-anonymous visitors), persisted in `localStorage` and reused thereafter, then
 * threaded onto the accountless conversation so a page visit ties to its eventual
 * conversation (`funnel_visit.visitor_id` = `conversation_fact.visitor_id`).
 *
 * Analytics-only and spoofable — NOT a security control. No React / no monitoring
 * imports so it stays trivially unit-testable and callable from the Redux listener.
 */

/** localStorage key holding the persisted visitor id. */
export const VISITOR_ID_STORAGE_KEY = 'natalie_visitor_id';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v: unknown): v is string => typeof v === 'string' && UUID_RE.test(v);

// The same id for every call within one page load even when localStorage is
// unavailable, so the two independent call sites — the visit beacon and the token
// mint — agree on one visitor_id (the funnel join key). Without this, a storage-blocked
// browser (e.g. Safari private mode, where setItem throws) would mint a *different* id
// per call and a visit would never correlate to its conversation.
let transientId: string | undefined;

/**
 * A UUID for the visitor id, guarding `crypto.randomUUID()` — it throws on old Safari
 * (<15.4) and in non-secure contexts, exactly the locked-down browsers this public-page
 * feature must not break (the token mint calls this synchronously). Mirrors the
 * guard-and-fallback in `devices/state/deviceTelemetry.listener.ts`. The fallback stays
 * UUID-shaped (unlike that episode id) so it still passes the backend's UUID validation
 * and correlates; `Math.random` is fine here because the id is analytics-only/spoofable,
 * never a security control.
 */
const generateVisitorId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch {
    // fall through to the non-crypto fallback below
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

/**
 * Return the persisted visitor id, minting + storing one on first call. Best-effort:
 * if `localStorage` is unavailable (SSR, privacy mode, quota) it still returns a valid
 * UUID and — via the module-level cache — keeps returning the *same* one for the rest of
 * this load, so a blocked-storage visitor's visit still correlates to its conversation
 * (it just won't persist across future loads). Never throws.
 */
export const getOrCreateVisitorId = (): string => {
  let store: Storage | undefined;
  try {
    store = typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    store = undefined; // access itself can throw (blocked cookies/storage)
  }

  try {
    const existing = store?.getItem(VISITOR_ID_STORAGE_KEY);
    if (isUuid(existing)) return existing;
  } catch {
    // read failed — fall through to the cache / mint path
  }

  const minted = transientId ?? generateVisitorId();
  transientId = minted;
  try {
    store?.setItem(VISITOR_ID_STORAGE_KEY, minted);
  } catch {
    // persist failed — the in-memory transientId keeps this load consistent
  }
  return minted;
};
